import React from "react";
import { Typography } from "antd";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import TableAbstract, { Column } from "stories/components/table/Table.abstract";
import type { TableProps as TableBaseProps } from "stories/components/table/TableBase";
import BaseTable from "stories/components/table/BaseTable";
import EmptyOverlay from "stories/components/table/EmptyOverlay";
import EllipsisCell, { ellipsisRender } from "stories/components/table/EllipsisCell";
import PermissionWrapper from "shared/components/PermissionWrapper";
import { useMercuryContext } from "context";
import { formatExpectedValue, RuleRecord } from "../rules";
import "./rules-table.scss";

export interface RulesTableProps<TRule extends RuleRecord = RuleRecord> {
  rules: TRule[];
  loading?: boolean;
  /** Adds the Deleted column (the list then includes deleted rules). */
  viewDeleted?: boolean;
  /** Show the Aggregate column. Defaults to whether any rule has one. */
  showAggregate?: boolean;
  /** No Edit / Delete columns — the matched-rules view of an evaluation. */
  readOnly?: boolean;
  onEdit?: (rule: TRule) => void;
  onDelete?: (rule: TRule) => void;
  canEdit?: boolean;
  canDelete?: boolean;
  /** Rendered in the table's actions bar (e.g. Add Rule, the view-deleted switch). */
  actions?: React.ReactNode;
  /**
   * Keys the table's remembered settings (column widths and order, page size,
   * hidden columns) — give each place its own, e.g. "project-type-rules".
   */
  id?: string;
  /** The heading in the table's bar. */
  title?: string;
  /** Height in px; defaults to fit the rows shown, between 3 and 12 rows. */
  height?: number;
  /** The app context (`useMercuryContext()` when omitted); stories pass a stub. */
  context?: any;
  className?: string;
}

export const check = (value: boolean) =>
  value ? (
    <CheckOutlined style={{ color: "var(--tertiary-color)" }} />
  ) : (
    <CloseOutlined style={{ color: "var(--red)" }} />
  );

type RuleRow<TRule extends RuleRecord> = TRule & { key: string };

type RulesTableAbstractProps<TRule extends RuleRecord> = Omit<TableBaseProps, "context"> & RulesTableProps<TRule> & { context: any };

/** Roomier than the base's 40: the cells hold links and tags, not just text. */
const ROW_HEIGHT = 46;
const HEADER_HEIGHT = 40;

/**
 * The rules of a definition on the global abstract table — resizable and
 * reorderable columns, the page-size pagination, column management and
 * export — over an in-memory list: `fetch` pages and sorts the rules it was
 * given rather than calling anything. Embedded in a tab or a modal, so the
 * full-page behaviours of the base (owning the document title and the body
 * scroll) are undone once it has initialised, as the adjustments details
 * page does.
 */
class RulesTableAbstract<TRule extends RuleRecord> extends TableAbstract<RuleRow<TRule>> {
  title: string;
  pageTitle: string;
  FilterComponents = [];
  defaultSortField = "createdAt";
  pageClass = "rulesTable";
  private documentTitle = typeof document !== "undefined" ? document.title : "";
  columns: Column<RuleRow<TRule>>[];

  constructor(props: RulesTableAbstractProps<TRule>) {
    super(props);
    this.title = props.title ?? "Rules";
    this.pageTitle = this.title;
    this.columns = this.buildColumns();
  }

  private get own(): RulesTableAbstractProps<TRule> {
    return this.props as RulesTableAbstractProps<TRule>;
  }

  private buildColumns(): Column<RuleRow<TRule>>[] {
    const { rules, viewDeleted = false, showAggregate, readOnly = false, onEdit, onDelete, canEdit = true, canDelete = true } = this.own;
    const aggregateColumn = showAggregate ?? rules.some((r) => !!r.aggregate);
    const link = (onClick: () => void, icon: React.ReactNode, label: string) => (
      <Typography.Link onClick={onClick}>
        {icon} {label}
      </Typography.Link>
    );

    return [
      { id: "targetEntity", title: "Target Entity", dataIndex: "targetEntity", width: 130, sorter: true },
      { id: "fieldPath", title: "Field Path", dataIndex: "fieldPath", width: 220, sorter: true, render: ellipsisRender() },
      ...(aggregateColumn
        ? [{ id: "aggregate", title: "Aggregate", dataIndex: "aggregate", width: 120, sorter: true, render: (value?: string) => value || "-" }]
        : []),
      { id: "conditionType", title: "Condition", dataIndex: "conditionType", width: 170, sorter: true },
      {
        id: "expectedValue",
        title: "Expected Value",
        dataIndex: "expectedValue",
        width: 200,
        render: (value: any) => <EllipsisCell text={formatExpectedValue(value)} />,
      },
      ...(readOnly
        ? []
        : [
            { id: "description", title: "Description", dataIndex: "description", width: 240, render: ellipsisRender() },
            { id: "active", title: "Active", dataIndex: "active", width: 70, align: "center" as const, sorter: true, render: (active: boolean) => check(active) },
            ...(viewDeleted
              ? [{ id: "deleted", title: "Deleted", dataIndex: "deleted", width: 70, align: "center" as const, sorter: true, render: (deleted: boolean) => check(deleted) }]
              : []),
            // The base keys columns by dataIndex, so each action column has its own
            {
              id: "edit",
              title: "Edit",
              dataIndex: "edit",
              width: 80,
              render: (_: any, r: RuleRow<TRule>) =>
                r.deleted ? null : (
                  <PermissionWrapper hasPermissions={canEdit}>{link(() => onEdit?.(r), <EditOutlined />, "Edit")}</PermissionWrapper>
                ),
            },
            {
              id: "delete",
              title: "Delete",
              dataIndex: "delete",
              width: 90,
              render: (_: any, r: RuleRow<TRule>) =>
                r.deleted ? null : (
                  <PermissionWrapper hasPermissions={canDelete}>{link(() => onDelete?.(r), <DeleteOutlined />, "Delete")}</PermissionWrapper>
                ),
            },
          ]),
    ];
  }

  /** The rules it was given, sorted by the chosen column and paged — no request. */
  async fetch(): Promise<any> {
    const { currentPage, itemsPerPage, sortField, sortOrder } = this.state;
    const field = sortField || this.defaultSortField;
    const direction = sortOrder === "descend" ? -1 : 1;
    const value = (r: any) => {
      const v = r[field];
      return v === undefined || v === null ? "" : typeof v === "boolean" ? Number(v) : v;
    };
    const sorted = this.own.rules
      .map((r) => ({ ...r, key: r._id }))
      .sort((a, b) => {
        const av = value(a), bv = value(b);
        return (typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv))) * direction;
      });
    const size = itemsPerPage || sorted.length || 1;
    const page = Math.min(Math.max(1, currentPage || 1), Math.max(1, Math.ceil(sorted.length / size)));
    const start = (page - 1) * size;
    return {
      data: {
        documents: sorted.slice(start, start + size),
        meta: { total: sorted.length, page, pages: Math.max(1, Math.ceil(sorted.length / size)), skip: start, limit: size },
      },
    };
  }

  renderActions() {
    return this.own.actions ?? null;
  }

  /** The base's table with a height that fits the rows shown (the base sizes itself to the viewport). */
  renderTable(actions?: any) {
    const { rowData } = this.state;
    const cols = this.columnMapping().map((item) => this.column(item as any));
    const allowSelection = (this.props.noSelect ? !this.props.noSelect : !!actions) && !this.isToolBarDisabled();
    const rows = Math.min(12, Math.max(3, rowData.length));
    const height = this.own.height ?? HEADER_HEIGHT + rows * ROW_HEIGHT + 22;

    return (
      <BaseTable
        ref={this.tableRef}
        className="new-projects-table"
        dataSource={rowData}
        columns={cols}
        onColumnSort={(sorter: any) => this.sort({}, {}, sorter, {} as any)}
        sortBy={{ key: this.state.sortField ?? "", order: this.state.sortOrder === "ascend" ? "asc" : "desc" }}
        loading={this.state.loading}
        rowClassName={(record: RuleRow<TRule>, index: number) => this.rowClassName(record, index)}
        rowHeight={ROW_HEIGHT}
        rowSelection={
          allowSelection
            ? { selectedRowKeys: this.state.selectedRows.map((r) => r.key), onSelectAll: this.selectAll, onSelect: this.select }
            : undefined
        }
        enabledRowSelection={typeof this.props.noSelect === "boolean" ? !this.props.noSelect : true}
        itemsPerPage={this.state.itemsPerPage}
        showFilters={this.state.showFilters}
        updateColumnWidth={this.updateColumnWidth}
        hasSidebar={this.own.hasSidebar}
        filtersRef={this.filtersRef}
        emptyRenderer={<EmptyOverlay reset={this.clear} loading={this.state.loading} />}
        height={height}
      />
    );
  }

  componentDidUpdate(prevProps: any, prevState: any) {
    super.componentDidUpdate(prevProps, prevState);

    // Embedded: the page keeps its scroll and its title (the base takes both on init).
    const body = document.querySelector("body");
    if (body) body.style.removeProperty("overflow");
    if (!prevState.initialised && this.state.initialised && this.documentTitle) document.title = this.documentTitle;

    const prev = prevProps as RulesTableAbstractProps<TRule>;
    const own = this.own;
    const columnsChanged = prev.viewDeleted !== own.viewDeleted || prev.readOnly !== own.readOnly || prev.showAggregate !== own.showAggregate || prev.canEdit !== own.canEdit || prev.canDelete !== own.canDelete;
    if (columnsChanged || prev.rules !== own.rules) {
      this.columns = this.buildColumns();
      if (this.state.initialised) this.getData();
    }
  }
}

/**
 * The rules of a definition on the global abstract table. One instance per
 * place with its own `id`, so each remembers its column layout and page size.
 */
function RulesTable<TRule extends RuleRecord = RuleRecord>(props: RulesTableProps<TRule>) {
  const live = useMercuryContext();
  const breaks = useBreakpoint();
  const Table = RulesTableAbstract as unknown as React.ComponentClass<RulesTableAbstractProps<TRule>>;
  return (
    <div className={`rules-table ${props.className ?? ""}`.trim()}>
      <Table
        {...props}
        id={props.id ?? "rules"}
        context={props.context ?? live}
        breaks={breaks as any}
        useLegacyEndpoints={false}
        hasSidebar={true}
        noSelect
        hideDefaultFilters
        rowClassName={(record: RuleRecord) => (record.deleted ? "row-deleted" : !record.active ? "row-inactive" : "")}
      />
    </div>
  );
}

export default RulesTable;
